/*
 * Driver Beacon — bookmarkable "Go Online" page.
 *
 * URL: /beacon/:key
 * Driver bookmarks this once. When on-shift, they open it + tap "Go Online".
 * Browser polls GPS every 30s and pushes to /api/tpt/beacon/:key/ping.
 * Closing the tab or tapping "Go Offline" stops broadcasting.
 *
 * No login. The 32-char beacon_key IS the auth (unguessable + revocable via admin).
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MedexOvaColdLoad from "../../components/MedexOvaColdLoad";
import axios from "axios";
import { toast, Toaster } from "sonner";
import usePushNotifications from "../../hooks/usePushNotifications";
import InstallPromptModal, { isStandalone } from "../../components/InstallPromptModal";
import { SonarPing, SlideDownCard, DrainRing, haptic } from "../../components/motion/MotionPrimitives";
import ErrorBoundary from "../../components/ErrorBoundary";
import "./driver-beacon.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tpt/beacon`;
const RIDER_API = `${process.env.REACT_APP_BACKEND_URL}/api/rider`;

const C = { forest: "#2B4C3B", bone: "#F9F8F6", slate: "#0F172A", slateDim: "#475569", rust: "#C55A44", line: "#E2E0D8", amber: "#B45309", ok: "#16A34A" };

const formatDetail = (d, f = "Something went wrong") => {
  if (!d) return f;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => (x && typeof x === "object" ? (x.msg || JSON.stringify(x)) : String(x))).join("; ");
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return String(d);
};


export default function DriverBeacon() {
  return (
    <ErrorBoundary name="driver-beacon">
      <DriverBeaconInner />
    </ErrorBoundary>
  );
}

function DriverBeaconInner() {
  const { key } = useParams();
  const [driver, setDriver] = useState(null);
  const [status, setStatus] = useState("offline");
  const [lastPing, setLastPing] = useState(null);
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState(null);
  const [pendingHail, setPendingHail] = useState(null); // {trip_id, rider_name, pickup, dropoff, fare_quote, offer_expires_at}
  const [activeRide, setActiveRide] = useState(null);   // consumer trip in ACCEPTED
  const [hailBusy, setHailBusy] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const push = usePushNotifications({ beaconKey: key });
  const [tab, setTab] = useState("home"); // "home" | "kpis" | "credentialing"
  const [installGateOpen, setInstallGateOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    if (isStandalone()) return false;
    try {
      const v = window.localStorage.getItem("astat.install.skip.driver");
      if (v === "1") return false;
    } catch { /* ignore */ }
    return true;
  });
  const watchRef = useRef(null);

  // Once installed (or user skipped), auto-prompt for notification permission
  useEffect(() => {
    if (installGateOpen) return;
    if (!push.supported) return;
    if (push.permission !== "default") return;
    const t = setTimeout(() => { push.enable().catch(() => {}); }, 900);
    return () => clearTimeout(t);
  }, [installGateOpen, push.supported, push.permission]);

  // Load Stripe Connect status once on mount + refresh whenever user returns
  // from the onboarding flow (URL will have ?payouts=done)
  useEffect(() => {
    if (!key) return;
    const params = new URLSearchParams(window.location.search);
    const returned = params.get("payouts");
    axios.get(`${RIDER_API}/payouts/driver/${encodeURIComponent(key)}/status`)
      .then((r) => {
        setPayoutStatus(r.data);
        if (returned === "done" && r.data.onboarded) toast.success("Payouts set up — earnings will auto-transfer to your bank.");
      })
      .catch(() => {/* silent */});
  }, [key]);

  // Earnings summary — refreshes on mount and whenever the driver returns from
  // a completed ride (activeRide transitions from truthy → null). Closes GW-8
  // by keeping earnings visible in-app instead of only inside the Stripe portal.
  const loadEarnings = React.useCallback(async () => {
    if (!key) return;
    try {
      const r = await axios.get(`${RIDER_API}/driver/${encodeURIComponent(key)}/earnings-summary`);
      setEarnings(r.data);
    } catch { /* silent */ }
  }, [key]);
  useEffect(() => { loadEarnings(); }, [loadEarnings]);
  useEffect(() => {
    if (!activeRide) loadEarnings();  // refresh after ride completes
  }, [activeRide, loadEarnings]);

  const startPayoutSetup = async () => {
    if (payoutBusy) return;
    setPayoutBusy(true);
    try {
      const { data } = await axios.post(`${RIDER_API}/payouts/driver/${encodeURIComponent(key)}/onboarding-link`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail, "Could not start payout setup"));
      setPayoutBusy(false);
    }
  };

  const openPayoutDashboard = async () => {
    try {
      const { data } = await axios.post(`${RIDER_API}/payouts/driver/${encodeURIComponent(key)}/dashboard-link`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail, "Could not open dashboard"));
    }
  };

  const [retryTick, setRetryTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/${key}`)
      .then(r => {
        if (cancelled) return;
        setDriver(r.data); setStatus(r.data.shift_status || "offline"); setErr(null);
      })
      .catch(e => {
        if (cancelled) return;
        const httpStatus = e?.response?.status;
        // Only a real 404 (unknown/revoked key) is a hard dead-end. Anything else
        // (network blip, 5xx) is transient — never permanently brick a valid link.
        if (httpStatus === 404) {
          setErr({ kind: "not_found", message: formatDetail(e?.response?.data?.detail, "Beacon not recognized") });
        } else {
          setErr({ kind: "transient", message: formatDetail(e?.response?.data?.detail, "Couldn't reach dispatch — check your connection") });
        }
      });
    return () => { cancelled = true; };
  }, [key, retryTick]);

  const setShift = async (s) => {
    try {
      await axios.post(`${API}/${key}/shift`, { shift_status: s });
      setStatus(s);
      if (s === "available") startTracking();
      else stopTracking();
      toast.success(`Now ${s}`);
    } catch (e) {
      const blocked = e?.response?.status === 403;
      toast.error(formatDetail(e?.response?.data?.detail, "Failed"), blocked ? { duration: 6000 } : undefined);
      if (blocked) setTab("credentialing");
    }
  };

  const pushPing = (p) => {
    axios.post(`${API}/${key}/ping`, { lat: p.coords.latitude, lng: p.coords.longitude, speed: p.coords.speed, heading: p.coords.heading })
      .then(() => setLastPing(new Date()))
      .catch(() => {});
    setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy });
  };

  const startTracking = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    if (watchRef.current != null) return;
    // Immediate ping + then a persistent watch
    navigator.geolocation.getCurrentPosition(pushPing, (e) => toast.error(e.message), { enableHighAccuracy: true, timeout: 10000 });
    watchRef.current = navigator.geolocation.watchPosition(pushPing, () => {}, { enableHighAccuracy: true, maximumAge: 15000 });
  };
  const stopTracking = () => {
    if (watchRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  };
  useEffect(() => () => stopTracking(), []);

  // Poll for pending consumer hails targeted at this driver. As soon as one
  // appears (status=OFFERED, source=consumer, assigned_driver=me), pop the alert.
  useEffect(() => {
    if (!driver) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${RIDER_API}/driver/${encodeURIComponent(key)}/pending-hails`);
        const list = data?.trips || [];
        const offered = list.find((t) => t.status === "OFFERED");
        const active = list.find((t) => t.status !== "OFFERED");
        if (stopped) return;
        setPendingHail((prev) => {
          if (!offered) return null;
          if (prev && prev.trip_id === offered.trip_id) return prev;
          // Fresh hail — haptic pulse + audible if possible
          haptic([120, 80, 120, 80, 200]);
          return offered;
        });
        setActiveRide(active || null);
      } catch (e) { /* keep trying */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(iv); };
  }, [driver, key]);

  const respondToHail = async (action) => {
    if (!pendingHail || hailBusy) return;
    setHailBusy(true);
    try {
      const { data } = await axios.post(
        `${RIDER_API}/driver-action/${pendingHail.trip_id}?action=${action}&beacon_key=${encodeURIComponent(key)}`
      );
      if (action === "accept") {
        toast.success("Ride accepted — opening navigation…");
        setPendingHail(null);
        // Redirect into the full Uber-style trip SPA (same UI as NEMT)
        if (data.driver_url) {
          window.location.href = data.driver_url;
        }
      } else {
        toast.info(data.status === "PROMOTED" ? "Declined — hail moved to next driver" : "Declined");
        setPendingHail(null);
      }
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail, `${action} failed`));
    } finally {
      setHailBusy(false);
    }
  };

  if (err) return (
    <div style={{ minHeight: "100vh", background: C.bone, display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 340 }}>
        <div style={{ fontSize: 36, color: C.rust, marginBottom: 8 }}>⌀</div>
        <div style={{ fontWeight: 700, marginBottom: 4 }} data-testid="beacon-error-title">
          {err.kind === "not_found" ? "Beacon unavailable" : "Connection issue"}
        </div>
        <div style={{ color: C.slateDim, fontSize: 13, marginBottom: 16 }} data-testid="beacon-error-message">{err.message}</div>
        {err.kind === "transient" ? (
          <button onClick={() => setRetryTick(t => t + 1)} data-testid="beacon-retry-btn" className="db-press"
            style={{ padding: "10px 20px", borderRadius: 10, background: C.forest, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        ) : (
          <div style={{ fontSize: 12, color: C.slateDim }}>Ask dispatch to resend your beacon link.</div>
        )}
      </div>
    </div>
  );
  if (!driver) return <MedexOvaColdLoad role="driver" minMs={1400} />;

  const isOnline = status === "available" || status === "loaded" || status === "busy";
  const statusColor = { available: C.ok, loaded: "#3A7BD5", busy: C.amber, offline: "#94A3B8" }[status];

  return (
    <div className="db-root" style={{ background: C.bone, color: C.slate }}>
      <Toaster position="top-center" richColors />
      {installGateOpen && (
        <InstallPromptModal
          audience="driver"
          onInstalled={() => setInstallGateOpen(false)}
          onSkip={() => {
            try { window.localStorage.setItem("astat.install.skip.driver", "1"); } catch { /* */ }
            setInstallGateOpen(false);
          }}
        />
      )}
      <div className="db-header">
        <div style={{ maxWidth: 420, margin: "0 auto", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: `linear-gradient(135deg,${C.forest},#3A7BD5)`, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800 }}>A</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim }}>MedexOVA · Driver Beacon</div>
            <div style={{ fontWeight: 800, fontSize: 20 }} data-testid="beacon-driver-name">{driver.name}</div>
          </div>
          <DriverGamifyBadge trips={driver.consumer_trips || driver.trip_count || 0} rating={driver.consumer_rating_avg || driver.rating || 5.0} />
        </div>
      </div>
      <div className="db-scroll-body" style={{ maxWidth: 420, margin: "0 auto" }}>

        <DriverPortalTabs tab={tab} onChange={setTab} credCleared={(driver.medexova_credentialing_status || "pending") === "cleared"} />

        {tab === "home" && (
        <>
        {/* Gamification strip — tenure + streak + daily goal */}
        <DriverGamifyStrip driver={driver} />

        {pendingHail && (
          <SlideDownCard open={!!pendingHail}>
            <div data-testid="beacon-hail-alert" style={{ background: "#FEF3C7", border: `3px solid ${C.amber}`, borderRadius: 14, padding: 18, marginBottom: 16, margin: "16px", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.amber, fontWeight: 800, marginBottom: 8 }}>🔔 New rider hail</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.slate, marginBottom: 6 }}>
                {pendingHail.rider_name || "Rider"} · ${(pendingHail.fare_quote?.total || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 13, color: C.slate, marginBottom: 4 }}>
                <b>Pickup:</b> {pendingHail.pickup?.address}
              </div>
              <div style={{ fontSize: 13, color: C.slate, marginBottom: 10 }}>
                <b>Dropoff:</b> {pendingHail.dropoff?.address}
              </div>
              <div style={{ fontSize: 12, color: C.slateDim, marginBottom: 12 }}>
                {pendingHail.fare_quote?.distance_mi} mi · ~{Math.round(pendingHail.fare_quote?.duration_min || 0)} min · {pendingHail.vehicle_type?.replace("_"," ")}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={() => respondToHail("decline")} disabled={hailBusy} data-testid="beacon-hail-decline" className="db-press"
                style={{ flex: 1, height: 46, borderRadius: 10, background: "#fff", color: C.rust, border: `2px solid ${C.rust}`, fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: hailBusy ? 0.6 : 1 }}>
                Decline
              </button>
              <DrainRing seconds={20} size={54} color={C.forest}>
                <button onClick={() => { haptic(80); respondToHail("accept"); }} disabled={hailBusy} data-testid="beacon-hail-accept" className="db-press"
                  style={{ width: 46, height: 46, borderRadius: "50%", background: C.forest, color: "#fff", border: "none", fontSize: 18, fontWeight: 800, cursor: "pointer", opacity: hailBusy ? 0.6 : 1 }}>
                  ✓
                </button>
              </DrainRing>
              <button onClick={() => { haptic(80); respondToHail("accept"); }} disabled={hailBusy} className="db-press"
                style={{ flex: 2, height: 46, borderRadius: 10, background: C.forest, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: hailBusy ? 0.6 : 1 }}>
                {hailBusy ? "Working…" : "Accept ride"}
              </button>
            </div>
            </div>
          </SlideDownCard>
        )}

        {activeRide && (
          <div data-testid="beacon-active-ride" style={{ background: "#fff", border: `3px solid ${C.forest}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.forest, fontWeight: 800, marginBottom: 6 }}>🚗 Active ride in progress</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.slate, marginBottom: 6 }}>
              {activeRide.rider_name || "Rider"} · ${(activeRide.fare_quote?.total || 0).toFixed(2)}
            </div>
            <div style={{ fontSize: 11, color: C.slateDim, marginBottom: 10 }}>
              Your driver app should be open in another tab. If you closed it, tap below to resume:
            </div>
            <a
              href={activeRide.driver_url_resume || "#"}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const r = await axios.get(`${RIDER_API}/rides/${activeRide.trip_id}/driver-resume?beacon_key=${encodeURIComponent(key)}`);
                  if (r.data?.driver_url) window.location.href = r.data.driver_url;
                } catch { toast.error("Couldn't reopen trip"); }
              }}
              data-testid="beacon-resume-ride"
              style={{ display: "block", textAlign: "center", padding: "12px 16px", borderRadius: 10, background: C.forest, color: "#fff", fontSize: 14, fontWeight: 700, textDecoration: "none" }}
            >
              Resume trip →
            </a>
          </div>
        )}

        {activeRide && (activeRide.workflow?.proof_artifacts || []).includes("chain_of_custody_report") && (
          <ChainOfCustodyCard trip={activeRide} beaconKey={key} apiBase={RIDER_API} />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>

        {payoutStatus && !payoutStatus.onboarded && (
          <div data-testid="beacon-payout-setup" style={{ background: "#FEF3C7", border: `3px solid ${C.amber}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.amber, fontWeight: 800, marginBottom: 6 }}>💰 Set up payouts</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.slate, marginBottom: 4 }}>Get paid automatically for every ride</div>
            <div style={{ fontSize: 12, color: C.slateDim, marginBottom: 10 }}>
              {payoutStatus.details_submitted
                ? "Almost there — finish the remaining Stripe verification steps."
                : "Add your bank details once (2 min). Your earnings then auto-transfer per ride."}
            </div>
            <button onClick={startPayoutSetup} disabled={payoutBusy} data-testid="beacon-payout-setup-btn"
              style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.forest, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: payoutBusy ? 0.6 : 1 }}>
              {payoutBusy ? "Opening…" : "Set up payouts →"}
            </button>
          </div>
        )}
        {payoutStatus && payoutStatus.onboarded && (
          <div data-testid="beacon-payout-ok" style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: C.forest, fontWeight: 800 }}>✓ Payouts active</div>
              <div style={{ fontSize: 12, color: C.slateDim }}>Earnings auto-transfer to your bank</div>
            </div>
            <button onClick={openPayoutDashboard} data-testid="beacon-payout-dashboard-btn"
              style={{ padding: "8px 12px", borderRadius: 8, background: "#fff", color: C.forest, border: `1px solid ${C.forest}`, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Manage payouts
            </button>
          </div>
        )}

        {/* In-app earnings — always visible when we have data. Not gated behind
         * Stripe onboarding: drivers who haven't onboarded still want to see
         * what they've earned (it's motivation to complete onboarding). */}
        {earnings && (
          <div data-testid="beacon-earnings-card" style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: earningsOpen ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 800 }}>Your earnings</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.slate }} data-testid="beacon-earnings-today-summary">
                  ${(earnings.today?.payout_usd || 0).toFixed(2)}
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.slateDim, marginLeft: 6 }}>today</span>
                </div>
              </div>
              <button onClick={() => setEarningsOpen(v => !v)} data-testid="beacon-earnings-toggle"
                style={{ padding: "8px 12px", borderRadius: 8, background: earningsOpen ? C.forest : "#fff", color: earningsOpen ? "#fff" : C.forest, border: `1px solid ${C.forest}`, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 200ms ease" }}>
                {earningsOpen ? "Hide" : "Details"}
              </button>
            </div>

            {earningsOpen && (
              <div data-testid="beacon-earnings-panel" style={{ paddingTop: 12, borderTop: `1px solid ${C.line}`, animation: "fadeIn 320ms ease" }}>
                {/* Today · Week · Month */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                  {[
                    ["Today", earnings.today, "beacon-earn-today"],
                    ["Week",  earnings.week,  "beacon-earn-week"],
                    ["Month", earnings.month, "beacon-earn-month"],
                  ].map(([label, val, tid]) => (
                    <div key={label} data-testid={tid}
                      style={{ background: C.bone, borderRadius: 10, padding: 10, textAlign: "left" }}>
                      <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 700 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: C.slate, marginTop: 2 }}>
                        ${(val?.payout_usd || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: 10, color: C.slateDim }}>{val?.rides || 0} {val?.rides === 1 ? "ride" : "rides"}</div>
                    </div>
                  ))}
                </div>

                {/* Tenure tier */}
                {earnings.tier && (
                  <div data-testid="beacon-earn-tier"
                    style={{ background: "linear-gradient(135deg,#2B4C3B,#3A7BD5)", color: "#fff", padding: 12, borderRadius: 10, marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 22 }}>{earnings.tier.badge}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.8 }}>Your tier</div>
                        <div style={{ fontSize: 14, fontWeight: 800 }}>{earnings.tier.label}</div>
                      </div>
                    </div>
                    {earnings.tier.rides_to_next != null && earnings.tier.next_label && (
                      <div style={{ fontSize: 11, opacity: 0.9, marginTop: 6 }}>
                        <b>{earnings.tier.rides_to_next}</b> more {earnings.tier.rides_to_next === 1 ? "ride" : "rides"} to <b>{earnings.tier.next_label}</b>
                      </div>
                    )}
                  </div>
                )}

                {/* Monthly fee */}
                {earnings.monthly_fee?.fee_usd > 0 && (
                  <div style={{ fontSize: 11, color: C.slateDim, marginBottom: 10 }} data-testid="beacon-earn-monthly-fee">
                    Platform fee this month: <b>${earnings.monthly_fee.fee_usd.toFixed(2)}</b> · <span style={{ textTransform: "capitalize" }}>{earnings.monthly_fee.bracket}</span> bracket · deducted at settlement
                  </div>
                )}

                {/* Encouragement to onboard if they haven't */}
                {payoutStatus && !payoutStatus.onboarded && (
                  <div style={{ fontSize: 11, color: C.amber, background: "#FEF3C7", padding: 8, borderRadius: 8 }} data-testid="beacon-earn-onboard-nudge">
                    You&apos;ve earned this money — set up payouts above to have it auto-transfer to your bank.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {push.supported && push.permission !== "granted" && (
          <div data-testid="beacon-push-setup" style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 800 }}>🔔 Push notifications</div>
              <div style={{ fontSize: 12, color: C.slateDim }}>Get pinged on new rides even with tab closed.</div>
            </div>
            <button
              onClick={async () => {
                const ok = await push.enable();
                if (ok) toast.success("Push notifications enabled");
                else if (push.permission === "denied") toast.error("Enable notifications in browser settings");
                else if (push.error) toast.error(push.error);
              }}
              disabled={push.enabling}
              data-testid="beacon-push-enable-btn"
              style={{ padding: "8px 12px", borderRadius: 8, background: C.forest, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", opacity: push.enabling ? 0.6 : 1 }}
            >
              {push.enabling ? "Enabling…" : "Turn on"}
            </button>
          </div>
        )}
        {push.supported && push.permission === "granted" && (
          <div data-testid="beacon-push-ok" style={{ background: "#F0FDF4", border: `1px solid #BBF7D0`, borderRadius: 14, padding: 10, fontSize: 12, color: C.forest, fontWeight: 700 }}>
            🔔 Push notifications on — you&apos;ll get pinged on new rides.
          </div>
        )}
        </div>

        {isOnline && (
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 16, marginTop: 16 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, marginBottom: 6 }}>Location tracking</div>
            {pos ? (
              <div style={{ fontSize: 13 }}>
                <div>lat: <b>{pos.lat.toFixed(5)}</b>, lng: <b>{pos.lng.toFixed(5)}</b></div>
                <div style={{ color: C.slateDim, fontSize: 12 }}>Accuracy ~{Math.round(pos.acc)}m</div>
                {lastPing && <div style={{ color: C.ok, fontSize: 12, marginTop: 6 }}>✓ Last ping {lastPing.toLocaleTimeString()}</div>}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: C.slateDim }}>Waiting for GPS lock…</div>
            )}
            <div style={{ marginTop: 12, fontSize: 11, color: C.slateDim }}>
              Keep this tab open while on shift. Dispatchers will see your live location when picking a driver for a trip.
            </div>
          </div>
        )}

        <div style={{ marginTop: 24, fontSize: 12, color: C.slateDim, textAlign: "center" }}>
          Bookmark this page for one-tap access next shift.
        </div>
        </>
        )}

        {tab === "kpis" && <DriverKPIsTab beaconKey={key} />}
        {tab === "credentialing" && <DriverCredentialingTab beaconKey={key} driver={driver} />}
      </div>

      <div className="db-bottom-bar">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
          {isOnline ? (
            <SonarPing color={statusColor} size={22} />
          ) : (
            <span style={{ width: 12, height: 12, borderRadius: "50%", background: statusColor, boxShadow: `0 0 0 4px ${statusColor}22` }}/>
          )}
          <span style={{ fontSize: 17, fontWeight: 800, textTransform: "capitalize" }} data-testid="beacon-status">{status}</span>
        </div>
        {!isOnline ? (
          <button onClick={() => { haptic(40); setShift("available"); }} data-testid="beacon-go-online" className="db-press"
            style={{ width: "100%", height: 54, borderRadius: 14, background: C.forest, color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: "0.04em", border: "none", cursor: "pointer", boxShadow: `0 10px 24px ${C.forest}44` }}>
            GO ONLINE
          </button>
        ) : (
          <button onClick={() => { haptic(40); setShift("offline"); }} data-testid="beacon-go-offline" className="db-press"
            style={{ width: "100%", height: 54, borderRadius: 14, background: C.rust, color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: "0.04em", border: "none", cursor: "pointer", boxShadow: `0 10px 24px ${C.rust}44` }}>
            GO OFFLINE
          </button>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Chain-of-Custody card (courier trips only)
// Shown when the active ride's workflow.proof_artifacts includes
// "chain_of_custody_report" (i.e. any Rule Package that declares
// tamper-seal capture — courier_rx, test_kit_drop, ...).
// Uses the same base64→server hash flow so the payload is auditable.
// ─────────────────────────────────────────────────────────────
function ChainOfCustodyCard({ trip, beaconKey, apiBase }) {
  const [busy, setBusy] = useState(null); // "pickup" | "dropoff" | "handoff"
  const [msg, setMsg] = useState(null);
  const coc = trip.chain_of_custody || {};
  const pickupSeal = coc.pickup_seal;
  const dropoffSeal = coc.dropoff_seal;

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const captureSeal = async (ceremony, file) => {
    if (!file) return;
    setBusy(ceremony);
    setMsg(null);
    try {
      const b64 = await readFileAsBase64(file);
      let lat = null, lng = null;
      if (navigator.geolocation) {
        await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => { lat = p.coords.latitude; lng = p.coords.longitude; res(); },
            () => res(),
            { enableHighAccuracy: true, timeout: 4000 }
          );
        });
      }
      const { data } = await axios.post(
        `${apiBase}/rides/${trip.trip_id}/chain-of-custody?beacon_key=${encodeURIComponent(beaconKey)}`,
        { ceremony, photo_base64: b64, lat, lng }
      );
      setMsg({ tone: "ok", text: `✓ ${ceremony} seal captured · sha256 ${data.sha256.slice(0, 8)}…` });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsg({ tone: "err", text: typeof detail === "string" ? detail : "Capture failed — try again." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="beacon-chain-of-custody" style={{ background: "#fff", border: `3px solid #B45309`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#B45309", fontWeight: 800, marginBottom: 8 }}>
        🔒 Chain of custody · {trip.cargo_type || "cargo"}
      </div>
      <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>
        {trip.workflow?.package_name || "Courier trip"} requires a tamper-seal photo at pickup and at drop-off. Both hashes land on the compliance packet.
      </div>
      <CocRow ceremony="pickup" label={`Pickup seal — ${trip.pickup?.address || "pharmacy handoff"}`} done={!!pickupSeal} doneAt={pickupSeal?.captured_at} busy={busy} onCapture={captureSeal} />
      <CocRow ceremony="dropoff" label={`Drop-off seal — ${trip.dropoff?.address || "recipient handoff"}`} done={!!dropoffSeal} doneAt={dropoffSeal?.captured_at} busy={busy} onCapture={captureSeal} />
      {msg && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: msg.tone === "ok" ? "#ECFDF5" : "#FEE2E2", color: msg.tone === "ok" ? "#047857" : "#991B1B", fontSize: 12, fontWeight: 600 }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function CocRow({ ceremony, label, done, doneAt, busy, onCapture }) {
  return (
    <div style={{ padding: 12, borderRadius: 10, background: done ? "#ECFDF5" : "#FEF3C7", border: `1px solid ${done ? "#10B981" : "#F59E0B"}`, marginBottom: 8 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: done ? "#047857" : "#92400E", fontWeight: 800, marginBottom: 6 }}>
        {done ? "✓ Captured" : "Awaiting capture"}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>{label}</div>
      {done && doneAt && (
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 6 }}>{new Date(doneAt).toLocaleString()}</div>
      )}
      {!done && (
        <label data-testid={`beacon-coc-${ceremony}-btn`} style={{ display: "block", padding: "10px 14px", borderRadius: 8, background: "#2B4C3B", color: "#fff", fontSize: 13, fontWeight: 700, textAlign: "center", cursor: "pointer", opacity: busy === ceremony ? 0.6 : 1 }}>
          {busy === ceremony ? "Uploading…" : "Take tamper-seal photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            disabled={busy === ceremony}
            onChange={(e) => onCapture(ceremony, e.target.files?.[0])}
          />
        </label>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Driver gamification · see /app/memory/DESIGN_LANGUAGE_v2.md §8.2
// Contract-safe (§TP-Ownership): tier labels apply universally; the
// payout-share tiers apply to ASTAT-INDEPENDENT drivers only. For
// TP-owned drivers this is a parallel-recognition display — the tier
// is shown but no payout implication is stated.
// ────────────────────────────────────────────────────────────────
const DRIVER_TIERS = [
  { label: "Legend",    min: 500, pct: 0.93, color: "#F59E0B" },
  { label: "Elite",     min: 250, pct: 0.90, color: "#F59E0B" },
  { label: "Veteran",   min: 100, pct: 0.85, color: "#F59E0B" },
  { label: "Contender", min: 25,  pct: 0.78, color: "#F59E0B" },
  { label: "Partner",   min: 0,   pct: 0.70, color: "#F59E0B" },
];
function tierFor(trips) {
  for (const t of DRIVER_TIERS) if (trips >= t.min) return t;
  return DRIVER_TIERS[DRIVER_TIERS.length - 1];
}
function nextTierFor(trips) {
  const cur = tierFor(trips);
  const idx = DRIVER_TIERS.findIndex(t => t.label === cur.label);
  return idx > 0 ? DRIVER_TIERS[idx - 1] : null;
}

function DriverGamifyBadge({ trips, rating }) {
  const t = tierFor(trips);
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999,
      background: `${t.color}1F`, border: `1px solid ${t.color}55`,
    }} data-testid="driver-tier-badge">
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: t.color, boxShadow: `0 0 6px ${t.color}` }}/>
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.16em", color: t.color, fontWeight: 800 }}>{t.label}</span>
      <span style={{ fontSize: 10, color: "#64748B", fontWeight: 700 }}>· {(rating || 5).toFixed(1)}★</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Driver Portal tabs — Home / KPIs / Credentialing
// ────────────────────────────────────────────────────────────────
function DriverPortalTabs({ tab, onChange, credCleared }) {
  const tabs = [
    ["home", "Home"],
    ["kpis", "KPIs"],
    ["credentialing", "Credentialing"],
  ];
  return (
    <div data-testid="driver-portal-tabs" style={{ display: "flex", gap: 6, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 5, marginBottom: 16 }}>
      {tabs.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`driver-portal-tab-${k}`}
          style={{
            flex: 1, padding: "9px 0", borderRadius: 10, border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 700, position: "relative",
            background: tab === k ? C.forest : "transparent",
            color: tab === k ? "#fff" : C.slateDim,
            transition: "all 180ms ease",
          }}>
          {label}
          {k === "credentialing" && !credCleared && (
            <span style={{ position: "absolute", top: 4, right: 10, width: 7, height: 7, borderRadius: "50%", background: tab === k ? "#fff" : C.amber }} data-testid="driver-portal-tab-credentialing-dot" />
          )}
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// KPIs tab — on-time %, completion %, acceptance %, trip count, rating
// ────────────────────────────────────────────────────────────────
function KpiStat({ label, value, testid, tone }) {
  return (
    <div data-testid={testid} style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: C.slateDim, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: tone || C.slate, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function DriverKPIsTab({ beaconKey }) {
  const [kpis, setKpis] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/${beaconKey}/kpis`)
      .then((r) => { if (!cancelled) setKpis(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatDetail(e?.response?.data?.detail, "Couldn't load your KPIs")); });
    return () => { cancelled = true; };
  }, [beaconKey]);

  if (err) return <div style={{ padding: 20, textAlign: "center", color: C.slateDim, fontSize: 13 }}>{err}</div>;
  if (!kpis) return <div style={{ padding: 20, textAlign: "center", color: C.slateDim, fontSize: 13 }}>Loading your numbers…</div>;

  const pct = (v) => (v == null ? "—" : `${v}%`);
  return (
    <div data-testid="driver-kpis-tab">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <KpiStat label="Trips completed" value={kpis.trip_count} testid="kpi-trip-count" />
        <KpiStat label="Rating" value={`${Number(kpis.rating || 5).toFixed(1)}★`} testid="kpi-rating" tone="#F59E0B" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
        <KpiStat label="On-time" value={pct(kpis.on_time_pct)} testid="kpi-on-time" tone={kpis.on_time_pct >= 90 ? "#16A34A" : undefined} />
        <KpiStat label="Completion" value={pct(kpis.completion_pct)} testid="kpi-completion" tone={kpis.completion_pct >= 90 ? "#16A34A" : undefined} />
        <KpiStat label="Acceptance" value={pct(kpis.acceptance_pct)} testid="kpi-acceptance" tone={kpis.acceptance_pct >= 80 ? "#16A34A" : undefined} />
      </div>
      {kpis.tier && (
        <div style={{ background: "linear-gradient(135deg,#2B4C3B,#3A7BD5)", color: "#fff", padding: 14, borderRadius: 12, marginBottom: 10 }} data-testid="kpi-tier-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 22 }}>{kpis.tier.badge}</div>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.8 }}>Tenure tier</div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{kpis.tier.label}</div>
            </div>
          </div>
        </div>
      )}

      {Array.isArray(kpis.trend) && kpis.trend.some((w) => w.completed > 0) && (
        <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 14, marginBottom: 10 }} data-testid="kpi-trend-card">
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: C.slateDim, fontWeight: 700, marginBottom: 10 }}>Last 8 weeks</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 60 }}>
            {(() => {
              const max = Math.max(1, ...kpis.trend.map((w) => w.completed));
              return kpis.trend.map((w) => (
                <div key={w.week_start} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }} data-testid={`kpi-trend-bar-${w.week_start}`}>
                  <div title={`${w.completed} trip(s) week of ${w.week_start}`}
                    style={{ width: "100%", borderRadius: 3, background: C.forest, opacity: w.completed ? 1 : 0.15, height: `${Math.max(4, (w.completed / max) * 48)}px` }} />
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: C.slateDim, textAlign: "center", marginTop: 8 }}>
        {kpis.accepted_count + kpis.declined_count > 0
          ? `${kpis.accepted_count} accepted · ${kpis.declined_count} declined`
          : "Acceptance rate fills in once you start responding to trip offers."}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Credentialing tab — status, expiry, plain-English notices feed
// ────────────────────────────────────────────────────────────────
const NOTICE_TONE = {
  ok:   { bg: "#F0FDF4", border: "#BBF7D0", fg: "#166534" },
  warn: { bg: "#FEF3C7", border: "#F5D48C", fg: "#92400E" },
  rust: { bg: "#FBE9E4", border: "#E8B6A8", fg: "#9A3412" },
};

function NoticeReplyControls({ beaconKey, notice, onReplied }) {
  const [flagging, setFlagging] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const send = async (action, msg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/${beaconKey}/credentialing/reply`, {
        notice_id: notice.id, notice_type: notice.type, action, message: msg || "",
      });
      toast.success(action === "flagged" ? "Flagged — your TP has been notified" : "Got it, marked as seen");
      setFlagging(false);
      onReplied?.();
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail, "Couldn't send that"));
    } finally { setBusy(false); }
  };

  if (notice.reply) {
    return (
      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, opacity: 0.85 }} data-testid={`notice-reply-status-${notice.type}`}>
        {notice.reply.action === "flagged" ? "🚩 You flagged this" : "✓ You acknowledged this"}
        {notice.reply.message && <span style={{ fontWeight: 400 }}> — &ldquo;{notice.reply.message}&rdquo;</span>}
        {notice.reply.resolved_at && (
          <div style={{ marginTop: 2, color: "#166534" }} data-testid={`notice-reply-resolved-${notice.type}`}>✅ Resolved by your TP</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {!flagging ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => send("acknowledged")} disabled={busy} data-testid={`notice-ack-${notice.type}`}
            style={{ fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
            Got it
          </button>
          <button onClick={() => setFlagging(true)} disabled={busy} data-testid={`notice-flag-${notice.type}`}
            style={{ fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
            Flag a problem
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. I already re-uploaded this document"
            data-testid={`notice-flag-message-${notice.type}`}
            style={{ width: "100%", fontSize: 12, borderRadius: 8, border: "1px solid rgba(0,0,0,0.2)", padding: 8, resize: "none" }} rows={2} />
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button onClick={() => send("flagged", message)} disabled={busy || !message.trim()} data-testid={`notice-flag-submit-${notice.type}`}
              style={{ fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, border: "none", background: "#9A3412", color: "#fff", cursor: "pointer" }}>
              Send to TP
            </button>
            <button onClick={() => setFlagging(false)} disabled={busy} data-testid={`notice-flag-cancel-${notice.type}`}
              style={{ fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "transparent", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DriverCredentialingTab({ beaconKey, driver }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const load = React.useCallback(() => {
    axios.get(`${API}/${beaconKey}/credentialing`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatDetail(e?.response?.data?.detail, "Couldn't load credentialing status")));
  }, [beaconKey]);
  React.useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ padding: 20, textAlign: "center", color: C.slateDim, fontSize: 13 }}>{err}</div>;
  if (!data) return <div style={{ padding: 20, textAlign: "center", color: C.slateDim, fontSize: 13 }}>Loading your credentialing status…</div>;

  const statusTone = data.status === "cleared" ? "ok" : data.status === "rejected" || data.status === "lapsed" ? "rust" : "warn";
  const t = NOTICE_TONE[statusTone];

  return (
    <div data-testid="driver-credentialing-tab">
      <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, marginBottom: 14 }} data-testid="credentialing-status-card">
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: t.fg, fontWeight: 800 }}>Credentialing status</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.fg, marginTop: 2, textTransform: "capitalize" }} data-testid="credentialing-status-value">
          {data.status === "lapsed" ? "Suspended" : data.status}
        </div>
      </div>

      {data.expiries.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: C.slateDim, fontWeight: 700, marginBottom: 6 }}>Document expiry</div>
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
            {data.expiries.map((e) => (
              <div key={e.field} style={{ padding: "10px 12px", borderBottom: `1px solid ${C.line}`, display: "flex", justifyContent: "space-between", fontSize: 13 }} data-testid={`expiry-row-${e.field}`}>
                <span>{e.label}</span>
                <b>{e.expires_on}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: C.slateDim, fontWeight: 700, marginBottom: 6 }}>Notices</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(data.notices || []).map((n, i) => {
          const nt = NOTICE_TONE[n.tone] || NOTICE_TONE.warn;
          return (
            <div key={n.id || i} style={{ background: nt.bg, border: `1px solid ${nt.border}`, borderRadius: 10, padding: 12 }} data-testid={`credentialing-notice-${n.type}`}>
              <div style={{ fontSize: 13, fontWeight: 700, color: nt.fg }}>{n.title}</div>
              <div style={{ fontSize: 12, color: nt.fg, marginTop: 2 }}>{n.body}</div>
              <div style={{ color: nt.fg }}>
                <NoticeReplyControls beaconKey={beaconKey} notice={n} onReplied={load} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverGamifyStrip({ driver }) {
  const trips = driver.consumer_trips || driver.trip_count || 0;
  const isIndependent = (driver.owner_type || "astat").toLowerCase() === "astat" && !driver.tp_id;
  const cur = tierFor(trips);
  const nxt = nextTierFor(trips);
  const pct = nxt ? Math.min(100, ((trips - cur.min) / (nxt.min - cur.min)) * 100) : 100;
  const toNext = nxt ? Math.max(0, nxt.min - trips) : 0;
  const streakWks = Math.min(52, Math.floor(trips / 5));
  const goalTarget = 5;
  const goalDone = 0;
  return (
    <div data-testid="driver-gamify-strip" style={{ display: "grid", gap: 10, marginBottom: 20 }}>
      {/* Tenure ring */}
      <div style={{
        padding: 16, borderRadius: 14,
        background: "linear-gradient(135deg,#0B1220 0%,#142433 100%)",
        border: "1px solid rgba(245,158,11,0.28)",
        boxShadow: "0 8px 20px -8px rgba(0,0,0,0.35)",
        color: "#E2E8F0",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#94A3B8", textTransform: "uppercase", fontWeight: 800 }}>Tenure</div>
            <div style={{ fontSize: 18, fontWeight: 900, marginTop: 2 }}>{cur.label}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#F59E0B" }}>{trips}</div>
            <div style={{ fontSize: 10, color: "#94A3B8", letterSpacing: "0.12em", textTransform: "uppercase" }}>trips</div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
          <div style={{
            height: "100%", width: `${pct}%`, background: "#F59E0B",
            boxShadow: "0 0 12px #F59E0B", transition: "width 700ms cubic-bezier(0.65,0,0.35,1)",
          }}/>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94A3B8", fontWeight: 700 }}>
          {isIndependent ? (
            <>
              <span>Current payout · {Math.round(cur.pct * 100)}%</span>
              {nxt && <span style={{ color: "#F59E0B" }}>{toNext} trips to {nxt.label} · {Math.round(nxt.pct * 100)}%</span>}
            </>
          ) : (
            <>
              <span>Parallel recognition · your TP handles pay</span>
              {nxt && <span style={{ color: "#F59E0B" }}>{toNext} trips to {nxt.label}</span>}
            </>
          )}
        </div>
      </div>
      {/* Streak + goal row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{
          padding: 12, borderRadius: 12,
          background: "#fff", border: "1px solid #E2E0D8",
        }}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#64748B", fontWeight: 800, marginBottom: 6 }}>Streak</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#F59E0B">
              <path d="M12 2s3 4 3 8a3 3 0 01-6 0c0-1 .5-2 1-3-1 1-2 2-3 4a5 5 0 1010 0c0-3-2-6-5-9z"/>
            </svg>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#0B1220" }}>{streakWks}</span>
            <span style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>wk on-time</span>
          </div>
        </div>
        <div style={{
          padding: 12, borderRadius: 12,
          background: "#fff", border: "1px solid #E2E0D8",
        }} data-testid="driver-daily-goal">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#64748B", fontWeight: 800 }}>Today</div>
            <div style={{ fontSize: 12, color: "#0B1220", fontWeight: 800 }}>{goalDone}/{goalTarget}</div>
          </div>
          <div style={{ fontSize: 11, color: "#334155", marginBottom: 6 }}>Hit 5 clean trips to keep the streak.</div>
          <div style={{ height: 3, background: "#E2E0D8", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(goalDone/goalTarget)*100}%`, background: "#F59E0B" }}/>
          </div>
        </div>
      </div>
    </div>
  );
}

